import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AppBarTitleService, AppBarActionsService, AppBarAction, EnumHelper, BankAccount, BankAccountType, BankAccountRole, MessageService, FormHelper, BankAccountHelper, EntityExData, AppPageService, EnumValue, HorizontalListData, SimpleDialogComponent, CommonActionKey } from 'common';
import { BankAccountRoleLabel } from '../bank.model';
import { MatDialog } from '@angular/material/dialog';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import { readWrite } from '../../user/user-permission/user-permission.data';
import { BankAccountService } from '../bank.service';
import { BankTransactionQueryResult } from '../../bank/bank.model';
import { ApplicationData } from '../../application/application.model';
import { AccountData } from '../../account/account.model';
import { BankingService } from '../banking.service';
import { BankAccountDetailsAdapter } from './bank-account-details.data';
import { ConfirmChangesDialogComponent } from '../../shared/confirm-changes-dialog/confirm-changes-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-bank-account-details',
    templateUrl: './bank-account-details.component.html',
    styleUrls: ['./bank-account-details.component.scss'],
    providers: [BankAccountDetailsAdapter],
    standalone: false
})
export class BankAccountDetailsComponent implements OnInit {
  bankAccountTypeOptions = EnumHelper.getNamesAndValues(BankAccountType);
  globalDesignationOptions: EnumValue[] = null;
  canEditBankAccount = false;
  canPullExtraInfo = false;
  accountType: string;
  form: UntypedFormGroup;
  linkStatus: number;
  canEditVerificationDate: boolean;
  setAsUncheckedAfterDate: Date;
  data: BankAccountDetailsComponentData;

  bankAccountListItems: HorizontalListData[];
  bankIntegrationListItems: HorizontalListData[];

  private saveSub: Subscription;

  readonly BankAccountRole = BankAccountRole;

  constructor(
    private readonly bankAccountService: BankAccountService,
    private readonly bankingService: BankingService,
    private readonly userPermissionService: UserPermissionService,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly adapter: BankAccountDetailsAdapter,
  ) {
    this.appBarActionsService.actions = [];

    this.form = this.formBuilder.group({
      businessName: { value: undefined, disabled: true },
      bankName: [undefined, Validators.compose([Validators.required, Validators.maxLength(128)])],
      bankAccountType: [undefined, Validators.required],
      accountNumber: [undefined, Validators.compose([Validators.required, Validators.maxLength(128)])],
      routingNumber: [undefined, Validators.maxLength(9)],
      verificationStatus: { value: undefined, disabled: true },
      lastCheckDate: new UntypedFormControl(null, { validators: null, updateOn: 'blur' }),
      screenShareRequired: undefined,
      bankAccountDesignationEntity: undefined
    });
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.dataInit.bind(this));
    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
    this.userPermissionService.granted([readWrite('servicing-bank-accounts')]).pipe(untilDestroyed(this))
      .subscribe(res => {
        this.canEditBankAccount = res;
        if (res)
          this.appBarActionsService.enable('edit', true);
      }),
      this.userPermissionService.granted([readWrite('servicing-bank-accounts-deletion')]).pipe(untilDestroyed(this))
        .subscribe(res => {
          if (res)
            this.appBarActionsService.enable('delete', true);
        }),
      this.userPermissionService.granted([readWrite('servicing-pull-synced-bank-account-extra-info')]).pipe(untilDestroyed(this))
        .subscribe(res => {
          this.canPullExtraInfo = res;
        })

    this.initFormValueChanges();
  }

  private initFormValueChanges(): void {
    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.enableSaveAction();
    });

    this.form.controls.lastCheckDate.valueChanges.pipe(untilDestroyed(this)).subscribe(input => {
      if (input) {
        const lastCheckDate = new Date(input);
        if (lastCheckDate >= this.setAsUncheckedAfterDate
          && this.form.controls.accountNumber.valid && this.form.controls.accountNumber.value.length > 3
          && this.form.controls.routingNumber.value && this.form.controls.routingNumber.value.length === 9) {
          this.form.controls.verificationStatus.setValue(true);
          if (!this.data.bankAccount || (this.data.bankAccount && !this.data.bankAccount.isVerified))
            this.form.controls.verificationStatus.markAsDirty();
        } else if (lastCheckDate < this.setAsUncheckedAfterDate) {
          this.form.controls.verificationStatus.setValue(false);
          if (!this.data.bankAccount || (this.data.bankAccount && this.data.bankAccount.isVerified))
            this.form.controls.verificationStatus.markAsDirty();
        }
      } else {
        this.form.controls.verificationStatus.setValue(false);
        if (!this.data.bankAccount || (this.data.bankAccount && this.data.bankAccount.isVerified))
          this.form.controls.verificationStatus.markAsDirty();
      }
    });

    this.form.controls.accountNumber.valueChanges.pipe(untilDestroyed(this)).subscribe(input => {
      if (!input || (input && input.length < 3)) {
        this.form.controls.verificationStatus.setValue(false);
        if (this.data.bankAccount && this.data.bankAccount.isVerified)
          this.form.controls.verificationStatus.markAsDirty();
      } else if (input && input.length > 3
        && (this.form.controls.routingNumber.value && this.form.controls.routingNumber.value.length === 9)
        && (new Date(this.form.controls.lastCheckDate.value) > this.setAsUncheckedAfterDate)) {
        this.form.controls.verificationStatus.setValue(true);
        if (!this.data.bankAccount && (this.data.bankAccount && !this.data.bankAccount.isVerified))
          this.form.controls.verificationStatus.markAsDirty();
      }
    });

    this.form.controls.routingNumber.valueChanges.pipe(untilDestroyed(this)).subscribe(input => {
      if (!input || (input && input.length !== 9)) {
        this.form.controls.verificationStatus.setValue(false);
        if (this.data.bankAccount && this.data.bankAccount.isVerified)
          this.form.controls.verificationStatus.markAsDirty();
      } else if ((input && input.length === 9)
        && (this.form.controls.accountNumber.valid && this.form.controls.accountNumber.value.length > 3)
        && (new Date(this.form.controls.lastCheckDate.value) > this.setAsUncheckedAfterDate)) {
        this.form.controls.verificationStatus.setValue(true);
        if (!this.data.bankAccount && (this.data.bankAccount && !this.data.bankAccount.isVerified))
          this.form.controls.verificationStatus.markAsDirty();
      }
    });
  }

  private dataInit(data: BankAccountDetailsComponentData): void {
    this.data = data;
    this.bankAccountListItems = this.adapter.adaptBankAccountsListData(data.bankAccount);
    this.bankIntegrationListItems = this.adapter.adaptBankIntegrationListData(data.bankAccount);

    const obj = data.account || data.application;

    this.form.reset({
      businessName: obj ? obj.entityName : data.entity ? data.entity.name : '',
      bankName: data.bankAccount ? data.bankAccount.bankName : '',
      accountNumber: data.bankAccount ? data.bankAccount.accountNumber : '',
      routingNumber: data.bankAccount ? data.bankAccount.routingNumber : '',
      bankAccountType: data.bankAccount ? data.bankAccount.accountType : '',
      verificationStatus: data.bankAccount ? data.bankAccount.isVerified : '',
      lastCheckDate: data.bankAccount ? data.bankAccount.lastCheckDate : '',
      screenShareRequired: data.bankAccount ? (data.bankAccount.screenShareRequired ? true : false) : false,
      bankAccountDesignationEntity: data.bankAccount ? data.bankAccount.entityRole : null
    });

    let bankAccountName = EnumHelper.getNameFromValue(BankAccountType, this.data.bankAccount?.accountType);
    if (!bankAccountName)
      bankAccountName = this.data.bankAccount?.accountType;

    this.accountType = bankAccountName ? bankAccountName : null;

    this.appBarTitleService.title = this.data.bankAccount
      ? (this.data.bankAccount.bankName ? this.data.bankAccount.bankName : '')
      + ' - ' + (this.accountType ? this.accountType : '') + ' '
      + BankAccountHelper.sanitizeBankAccountNumber(this.data.bankAccount.accountNumber)
      : 'Add new bank account';

    this.linkStatus = this.data && this.data.bankAccount ? this.data.bankAccount.linkingStatus : null;
    this.setAsUncheckedAfterDate = (this.data && this.data.bankAccount && this.data.bankAccount.setAsUncheckedAfterDate)
      ? new Date(this.data.bankAccount.setAsUncheckedAfterDate) : null;

    this.setFormState();
    this.setBankAccountTypeOptions();
    this.setAppBarActions();
  }

  private setFormState(): void {
    if (this.data.viewMode && this.data.bankAccount) {
      this.form.disable();
    } else if (this.data.bankAccount && !this.data.viewMode) {
      if (!this.data.bankAccount.isBasicDataEditable) {
        this.form.controls.bankName.disable();
        this.form.controls.bankAccountType.disable();
        this.form.controls.routingNumber.disable();
      }

      this.userPermissionService.granted([readWrite('servicing-entities-sensitive-data')])
        .subscribe(res => {
          if (!res || !this.data.bankAccount.isBasicDataEditable)
            this.form.controls.accountNumber.disable();
        });
    }

    this.userPermissionService.granted([readWrite('servicing-bank-accounts-verification')])
      .subscribe(res => {
        if (this.data.viewMode && this.data.bankAccount)
          this.canEditVerificationDate = false;
        else this.canEditVerificationDate = res;
      });
  }

  private setBankAccountTypeOptions(): void {
    const currentGlobalDesignation: BankAccountRole = (this.data.bankAccount && this.data.account &&
      this.data.account.id)
      ? this.data.bankAccount.entityRole : null;
    this.globalDesignationOptions = this.filterDesignationOptions(currentGlobalDesignation);
  }

  cancel(): void {
    this.appPageService.back();
  }

  edit(): void {
    if (this.data.account) {
      if (this.route.snapshot.queryParams.uniqueId)
        // eslint-disable-next-line max-len
        this.router.navigate([`/account/${this.data.account.id}/bank-account/${this.data.bankAccount.id}/edit`], { replaceUrl: true, queryParams: { uniqueId: this.route.snapshot.queryParams.uniqueId } });
      else
        this.router.navigate([`/account/${this.data.account.id}/bank-account/${this.data.bankAccount.id}/edit`], { replaceUrl: true });
    } else if (this.data.application)
      // eslint-disable-next-line max-len
      this.router.navigate([`/application/${this.data.application.id}/bank-account/${this.data.bankAccount.id}/edit`], { replaceUrl: true });
    else if (this.data.entity)
      this.router.navigate([`/company/${this.data.entity.id}/banks/${this.data.bankAccount.id}/edit`], { replaceUrl: true });
    else return;
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);

    if (this.saveSub) {
      return;
    }

    if (!this.data || (!this.data.account && !this.data.entity && !this.data.application) || !this.form.valid) {
      return;
    }

    const confirmChangesDialogData = this.adapter.adaptConfirmChangesData(this.form, this.data.bankAccount);
    ConfirmChangesDialogComponent.show(this.dialog, confirmChangesDialogData)
      .subscribe((result: boolean) => {
        const command: BankAccount = {
          id: this.data.bankAccount ? this.data.bankAccount.id : null,
          bankName: this.form.controls.bankName.value,
          routingNumber: this.form.controls.routingNumber.value,
          accountNumber: this.form.controls.accountNumber.value,
          accountType: this.form.controls.bankAccountType.value,
          lastCheckDate: this.form.controls.lastCheckDate.value,
          screenShareRequired: this.form.controls.screenShareRequired.value,
          entityRole: this.form.controls.bankAccountDesignationEntity.value ?? BankAccountRole.None
        };
        const _this = this;
        const entityId = _this.data?.entity?.id || _this.data?.account?.entityId || _this.data?.application?.entityId;
        if (result && this.data.bankAccount) {
          this.saveSub = _this.bankAccountService
            .updateBankAccount(entityId, command)
            .subscribe(_this.saveSuccessHandler.bind(_this), _this.saveErrorHandler.bind(_this));
        } else if (result && !_this.data.bankAccount) {
          this.saveSub = _this.bankAccountService
            .createBankAccount(entityId, command)
            .subscribe(_this.saveSuccessHandler.bind(_this), _this.saveErrorHandler.bind(_this));
        }
      });
  }

  delete(): void {
    if (!this.data.bankAccount || !this.data.bankAccount.id || !this.canEditBankAccount) {
      return;
    }

    const haveAnyRole = this.data.entityLoans && Object.keys(this.data.entityLoans).length !== 0 && this.data.bankAccount.entityRole !== BankAccountRole.None;

    if (haveAnyRole) {
      SimpleDialogComponent.show(this.dialog, this.adapter.adaptCannotDeleteDialogData(this.data.bankAccount))
        .subscribe(() => this.detailPage());
    } else {
      SimpleDialogComponent.show(this.dialog, this.adapter.adaptDeleteDialogData(this.data.bankAccount))
        .subscribe((actionKey: CommonActionKey) => {
          if (actionKey === CommonActionKey.Confirm) {
            const companyId = this.data?.account?.entityId || this.data?.entity?.id || this.data?.application?.entityId;
            this.bankAccountService.deleteBankAccount(companyId, this.data.bankAccount.id)
              .subscribe(() => this.detailPage());
          }
        });
    }
  }

  syncExtraInfo(): void {
    if (!this.canPullExtraInfo) {
      return;
    }

    const entityId = this.data.account ? this.data.account.entityId : (this.data.entity ? this.data.entity.id : this.data.application.entityId);
    const accountId = this.data.bankAccount && this.data.bankAccount.syncedBankAccount
      ? this.data.bankAccount.syncedBankAccount.id : null;
    this.bankingService.synchronizeBankAccountExtraInfo(entityId, accountId)
      .subscribe(
        res => {
          this.data.bankAccount = res;

          this.form.controls.accountNumber.reset(res.accountNumber);
          this.form.controls.routingNumber.reset(res.routingNumber);
          SimpleDialogComponent.show(this.dialog, this.adapter.adaptSyncSuccessDialogData(this.data.bankAccount));
        },
        err => {
          SimpleDialogComponent.show(this.dialog, this.adapter.adaptSyncErrorDialogData(this.data.bankAccount, err));
        })
  }

  private detailPage(): void {
    if (this.data.account)
      this.router.navigate([`/account/${this.data.account.id}/bank-account`], { replaceUrl: true });
    else if (this.data.application)
      this.router.navigate([`/application/${this.data.application.id}/bank-accounts`], { replaceUrl: true });
    else if (this.data.entity)
      this.router.navigate([`/company/${this.data.entity.id}/banks`], { replaceUrl: true });
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveSuccessHandler(): void {
    this.saveSubClear();
    if (this.data.account) {
      this.router.navigate([`/account/${this.data.account.id}/bank-account`], { replaceUrl: true });
    } else if (this.data.application)
      this.router.navigate([`/application/${this.data.application.id}/bank-accounts`], { replaceUrl: true });
    else if (this.data.entity)
      this.router.navigate([`/company/${this.data.entity.id}/banks`], { replaceUrl: true });
    else this.appPageService.back();
  }

  private filterDesignationOptions(currentDesignation: BankAccountRole): EnumValue[] {
    let designationOptions = EnumHelper.getMappedNamesAndValues(BankAccountRole, BankAccountRoleLabel).filter(_ => _.name !== undefined);
    const paymentAndDisbursement = designationOptions.filter(_ => _.value === BankAccountRole.PaymentAndDisbursement);

    if (currentDesignation === BankAccountRole.PaymentAndDisbursement) {
      designationOptions = paymentAndDisbursement;
    } else if (currentDesignation === BankAccountRole.Payment || currentDesignation === BankAccountRole.Disbursement) {
      designationOptions = paymentAndDisbursement;
      designationOptions.push({ name: EnumHelper.getNameFromValue(BankAccountRole, currentDesignation), value: currentDesignation });
    }

    return designationOptions;
  }

  private setAppBarActions(): void {
    if (!this.data.bankAccount) {
      this.appBarActionsService.actions.push(
        { id: 'cancel', label: 'Cancel', buttonType: 'button' },
        { id: 'save', label: 'Save', disabled: false, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' });
    } else if (this.data.bankAccount && this.data.viewMode) {
      this.appBarActionsService.actions.push(
        { id: 'edit', label: 'Edit', disabled: true, buttonType: 'button' },
        { id: 'save', label: 'Save', disabled: true, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' });
    } else if (this.data.bankAccount && !this.data.viewMode) {
      this.appBarActionsService.actions.push(
        { id: 'cancel', label: 'Cancel', buttonType: 'button' },
        { id: 'delete', label: 'Delete', disabled: true, buttonType: 'button' },
        { id: 'save', label: 'Save', disabled: true, buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary' });
    }
  }

  private enableSaveAction(): void {
    if (!this.data.viewMode && this.form.dirty && this.canEditBankAccount) {
      this.appBarActionsService.enable('save', true);
    }
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  clearDate(fc: string): void {
    if (!fc) {
      return;
    }

    this.form.get(fc).markAsDirty();
    this.enableSaveAction();
  }
}

export interface BankAccountDetailsComponentData {
  bankAccount: BankAccount;
  account?: AccountData;
  application?: ApplicationData;
  viewMode?: boolean;
  entity?: EntityExData;
  entityLoans?: AccountData[];
  bankTransactionQueryResult?: BankTransactionQueryResult;
}
